import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet } from '../shared/Sheet';
import { Icon } from '../chrome/Icon';
import { useT } from '../../i18n';
import { api, ApiError } from '../../api';
import { qk } from '../../data/keys';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface BlockedBaby {
  baby_id: string;
  name: string;
  other_members: number;
}

/** "Delete Account" flow reached from ProfileSheet's sign-out row (App Store
 * Guideline 5.1.1(v) / Play Store equivalent). Explains the 30-day grace
 * period, confirms inline, then either starts deletion (the app-wide
 * AccountDeletionScreen gate takes over once /me refetches) or shows which
 * shared babies need their other members removed first. Mirrors the iOS
 * DeleteAccountSheet. */
export function DeleteAccountSheet({ open, onClose }: Props) {
  const t = useT();
  const queryClient = useQueryClient();

  const [confirming, setConfirming] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [blockingBabies, setBlockingBabies] = useState<BlockedBaby[] | null>(null);

  useEffect(() => {
    if (open) {
      setConfirming(false);
      setErrorMsg(null);
      setBlockingBabies(null);
    }
  }, [open]);

  const requestDeletion = async () => {
    if (requesting) return;
    setRequesting(true);
    setErrorMsg(null);
    try {
      await api('/me/delete', { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: qk.me() });
      // AccountDeletionScreen takes over as soon as /me reflects the new
      // deletion_requested_at — just close this sheet.
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as { error?: { babies?: BlockedBaby[] } } | undefined;
        setBlockingBabies(body?.error?.babies ?? []);
        setConfirming(false);
      } else {
        console.error(e);
        setErrorMsg(t('account.deletion.requestError', 'Could not start deletion. Please try again.'));
        setConfirming(false);
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} size="md" maxHeight="92dvh" label={t('account.deletion.title', 'Delete Account')}>
      <div className="flex flex-col gap-6 px-6 pb-8 pt-0 overflow-y-auto">
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('account.deletion.title', 'Delete Account')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        {blockingBabies ? (
          <>
            <h1 className="font-serif text-[24px] leading-tight tracking-tight text-ink">
              {t('account.deletion.blockedTitle', 'Remove other members first')}
            </h1>
            <p className="text-[14.5px] leading-relaxed text-ink-2 -mt-2">
              {t(
                'account.deletion.blockedDesc',
                "You're the owner of these babies and other people still have access. Remove them from Sharing, then come back here."
              )}
            </p>
            <div className="overflow-hidden rounded-xl border border-line bg-surface">
              {blockingBabies.map((b, i) => (
                <div
                  key={b.baby_id}
                  className={[
                    'flex items-center justify-between px-4 py-3.5',
                    i > 0 ? 'border-t border-hairline' : '',
                  ].join(' ')}
                >
                  <span className="text-[14.5px] text-ink">{b.name}</span>
                  <span className="text-[12.5px] text-ink-3">
                    {b.other_members === 1
                      ? t('account.deletion.oneOtherMember', '1 other member')
                      : t('account.deletion.otherMembers', '{n} other members').replace('{n}', String(b.other_members))}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif text-[24px] leading-tight tracking-tight text-ink">
              {confirming
                ? t('account.deletion.confirmTitle', 'Are you sure?')
                : t('account.deletion.introTitle', 'This will remove your data')}
            </h1>
            <p className="text-[14.5px] leading-relaxed text-ink-2 -mt-2">
              {confirming
                ? t(
                    'account.deletion.confirmDesc',
                    'This starts a 30-day countdown. You can cancel any time before then by signing back in.'
                  )
                : t(
                    'account.deletion.introDesc',
                    'Deleting your account removes your profile, babies you solely own, and everything in them — milestones, growth records, photos, and comments. You’ll have 30 days to change your mind.'
                  )}
            </p>

            {errorMsg && (
              <div
                role="alert"
                className="rounded-xl px-4 py-3 text-[13px] leading-snug flex items-start gap-2"
                style={{
                  background: 'rgba(181, 82, 59, 0.10)',
                  border: '1px solid rgba(181, 82, 59, 0.22)',
                  color: '#7E3320',
                }}
              >
                <span className="shrink-0">⚠️</span>
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => (confirming ? requestDeletion() : setConfirming(true))}
                disabled={requesting}
                className="inline-flex w-full min-h-14 items-center justify-center gap-2.5 rounded-3xl px-6 text-[15.5px] font-semibold text-white disabled:opacity-50 active:scale-[0.98] transition-all cursor-pointer"
                style={{ background: '#B5523B' }}
              >
                {requesting ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent animate-spin rounded-full" />
                ) : confirming ? (
                  t('account.deletion.confirmCTA', 'Yes, delete my account')
                ) : (
                  t('account.deletion.startCTA', 'Delete My Account')
                )}
              </button>
              {confirming && (
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="text-[13px] font-semibold text-ink-3 hover:text-ink-2 underline"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
